// callAssignCell.ts — pure helpers for assigning a call to a deal / nested
// project directly from the Calls list "DEAL OR PROJECT" cell (calls-list-assign-v1).
// Framework-agnostic (no React, no fetch) so the owner-PATCH body, optimistic
// row update, rollback, and cell-label derivation can be unit-tested against
// REAL GET /api/crm/calls wire shapes (see callAssignCell.test.ts).
//
// THE SHARP TRAP (shared with dealRollup.isProjectOwnedRow / the review PATCH):
// owner_type_id === 3 is a PROJECT only when owner_id is a non-empty string that
// isn't "0". A deal owner is owner_type_id === 2 with a numeric owner_id. This
// module never inspects owner_type_id alone — the CELL keys off the server's
// enrichment fields (linked_type / linked_label / linked_hash), which the worker
// already resolved through that predicate.

/** Runtime marker (minification-proof string literal) for live-verify + data-marker. */
export const CALLS_LIST_ASSIGN_MARKER = 'calls-list-assign-v1'

/** A staged deal- or project-owner selection. `null` means "clear the owner". */
export type CallOwnerSelection =
  | { type: 'deal' | 'project'; id: string; label: string; sublabel?: string; parentDealId?: number | string | null }
  | null

/** The owner-only PATCH body sent to /api/crm/rc/activities/:id/review. */
export type CallOwnerPatch =
  | { clearOwner: true }
  | { ownerTypeId: 2; ownerId: number | string }
  | { ownerTypeId: 3; ownerId: string }

/**
 * Map a staged selection to the review-PATCH owner body. Mirrors the phone
 * wrap-up `assignOwnerPatch` EXACTLY so both surfaces hit the same server
 * contract (both-or-neither pairing; project ownerId is a TEXT id; a deal
 * ownerId stays numeric when it parses, else the raw id).
 */
export function ownerPatchFromSelection(sel: CallOwnerSelection): CallOwnerPatch {
  if (!sel) return { clearOwner: true }
  if (sel.type === 'project') return { ownerTypeId: 3, ownerId: String(sel.id) }
  const oid = Number(sel.id)
  return { ownerTypeId: 2, ownerId: Number.isFinite(oid) ? oid : sel.id }
}

/** The subset of a call row this module reads / writes for the cell. */
export interface CallOwnerRowFields {
  id: number
  owner_type_id?: number | null
  owner_id?: number | string | null
  linked_type?: 'deal' | 'lead' | 'project' | 'contact' | null
  linked_label?: string | null
  linked_hash?: string | null
}

/**
 * The optimistic field patch to merge into a call row so the DEAL OR PROJECT
 * cell reflects a fresh assignment without a reload. Deep-link hashes mirror the
 * server enrichment in GET /api/crm/calls verbatim:
 *   deal    → /deals/record/<id>
 *   project → /projects/record/<encodeURIComponent(id)>
 * Clearing nulls the owner + linked_* so the cell falls back to "+ Assign".
 */
export interface OptimisticOwnerFields {
  owner_type_id: number
  owner_id: number | string | null
  linked_type: 'deal' | 'project' | null
  linked_label: string | null
  linked_hash: string | null
}

export function optimisticOwnerFields(sel: CallOwnerSelection): OptimisticOwnerFields {
  if (!sel) {
    return { owner_type_id: 0, owner_id: null, linked_type: null, linked_label: null, linked_hash: null }
  }
  if (sel.type === 'project') {
    const id = String(sel.id)
    return {
      owner_type_id: 3,
      owner_id: id,
      linked_type: 'project',
      linked_label: sel.label || `Project ${id}`,
      linked_hash: `/projects/record/${encodeURIComponent(id)}`,
    }
  }
  const oid = Number(sel.id)
  const id = Number.isFinite(oid) ? oid : sel.id
  return {
    owner_type_id: 2,
    owner_id: id,
    linked_type: 'deal',
    linked_label: sel.label || `Deal #${id}`,
    linked_hash: `/deals/record/${id}`,
  }
}

/**
 * Return a NEW rows array with the target call's owner + linked_* replaced by
 * the optimistic projection of `sel`. Non-target rows are returned by reference
 * (structural sharing). The caller keeps the previous array to roll back on a
 * failed PATCH. Pure — never mutates the input.
 */
export function applyCallOwnerOptimistic<T extends CallOwnerRowFields>(
  calls: T[] | null | undefined,
  callId: number,
  sel: CallOwnerSelection,
): T[] {
  const patch = optimisticOwnerFields(sel)
  return (calls || []).map((c) => (c && c.id === callId ? { ...c, ...patch } : c))
}

/**
 * Roll a single call row back to a captured prior snapshot after a failed PATCH.
 * TARGETED + FUNCTIONAL: restores ONLY the one call's prior row into the CURRENT
 * array, so any unrelated callsData change that landed between the optimistic
 * write and the failure (a page/filter refetch, a second assignment) is
 * preserved — never clobbered by a whole-array snapshot restore.
 *
 * - `prevRow` null/undefined → nothing to restore; returns the input by reference.
 * - the target row is GONE from the current array (refetched away) → do nothing;
 *   returns the input by reference (the stale prior row is not re-inserted).
 * - otherwise → a NEW array with the target id replaced by `prevRow`.
 * Pure — never mutates the input.
 */
export function restoreCallOwnerRow<T extends CallOwnerRowFields>(
  calls: T[] | null | undefined,
  callId: number,
  prevRow: T | null | undefined,
): T[] {
  const rows = calls || []
  if (!prevRow) return rows as T[]
  if (!rows.some((c) => c && c.id === callId)) return rows as T[]
  return rows.map((c) => (c && c.id === callId ? prevRow : c))
}

/** The cell's display model: an assigned chip (deal/lead/project) or the empty state. */
export type CallAssignCellModel =
  | { assigned: true; type: 'deal' | 'lead' | 'project'; label: string; hash: string }
  | { assigned: false }

/**
 * Derive what the DEAL OR PROJECT cell should render. A row is "assigned" only
 * when the server enrichment resolved a deal / lead / project label + deep link
 * (contact is NEVER shown here — it has its own column). Everything else — a
 * contact-only owner_type_id 3, an unowned row, a dangling id the enrichment
 * couldn't resolve — is the empty "+ Assign" state.
 */
export function callAssignCellModel(row: CallOwnerRowFields | null | undefined): CallAssignCellModel {
  if (!row) return { assigned: false }
  const t = row.linked_type
  if ((t === 'deal' || t === 'lead' || t === 'project') && row.linked_label && row.linked_hash) {
    return { assigned: true, type: t, label: row.linked_label, hash: row.linked_hash }
  }
  return { assigned: false }
}
